import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TabProvider, useTab } from 'contexts/TabContext';
import { useToast } from 'contexts/ToastContext';
import { setToastHandlers } from 'utils/toastBridge';
import { FavoritesProvider } from 'contexts/FavoritesContext';
import { useAuth } from 'contexts/AuthContext';
import {
  HR_TOP_MENUS,
  HR_LEFT_MENUS,
  HR_HOME_TAB,
  filterLeftMenusForUser,
  filterTopMenus,
} from 'config/hrMenuConfig';
import Sidebar from './Sidebar';
import WorkspaceTabBar from './WorkspaceTabBar';
import ToastContainer from 'components/common/ToastContainer';
import ConfirmDialog from 'components/common/ConfirmDialog';
import {
  LayoutContainer,
  Footer,
  DashboardContainer,
  DashboardBody,
  ContentArea,
} from 'styles/common.styles';

/** ERP HR 전용 레이아웃 */
const HrLayoutInner = () => {
  const { showAlert, showConfirm } = useToast();
  const { openTab } = useTab();
  const { isMaster } = useAuth();

  // masterOnly 화면(기관등록 등)은 시스템 마스터에게만 노출
  const leftMenus = useMemo(
    () => filterLeftMenusForUser(HR_LEFT_MENUS, { isMaster }),
    [isMaster]
  );
  const topMenuList = useMemo(() => filterTopMenus(HR_TOP_MENUS), []);
  const [activeTopMenu, setActiveTopMenu] = useState(
    () => filterTopMenus(HR_TOP_MENUS)[0]?.id || '200'
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [menuSearchFocusNonce, setMenuSearchFocusNonce] = useState(0);

  useEffect(() => {
    setToastHandlers({ showAlert, showConfirm });
  }, [showAlert, showConfirm]);

  useEffect(() => {
    const onTabClosed = (event) => {
      const msg = event?.detail?.message;
      if (msg) showAlert(msg, 'info', 4000);
    };
    window.addEventListener('erp:workspace-tab-closed', onTabClosed);
    return () => window.removeEventListener('erp:workspace-tab-closed', onTabClosed);
  }, [showAlert]);

  // Ctrl/Cmd+F → 사이드바 펼친 뒤 메뉴 검색 포커스
  useEffect(() => {
    const onKeyDown = (e) => {
      const key = String(e.key || '').toLowerCase();
      const code = String(e.code || '');
      const isF = key === 'f' || code === 'KeyF';
      if (!isF) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.altKey || e.shiftKey) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') {
        e.stopImmediatePropagation();
      }
      setSidebarCollapsed(false);
      setMenuSearchFocusNonce((n) => n + 1);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useEffect(() => {
    openTab({ ...HR_HOME_TAB });
  }, [openTab]);

  const handleTopMenuClick = useCallback((menuId) => {
    setActiveTopMenu(menuId);
  }, []);

  return (
    <LayoutContainer>
      <DashboardContainer>
        <DashboardBody>
          <Sidebar
            activeTopMenu={activeTopMenu}
            allLeftMenus={leftMenus}
            topMenus={topMenuList}
            onSwitchTopMenu={handleTopMenuClick}
            searchFocusNonce={menuSearchFocusNonce}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
            onExpandSidebar={() => setSidebarCollapsed(false)}
          />
          <ContentArea>
            <WorkspaceTabBar />
          </ContentArea>
        </DashboardBody>
      </DashboardContainer>

      <Footer>전사적자원관리 토탈 솔루션</Footer>
      <ToastContainer />
      <ConfirmDialog />
    </LayoutContainer>
  );
};

const HrLayout = () => (
  <TabProvider enforceOpenLimit>
    <FavoritesProvider>
      <HrLayoutInner />
    </FavoritesProvider>
  </TabProvider>
);

export default HrLayout;
